import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Badge,
    Card,
    Col,
    Container,
    Row,
} from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { NavLink } from 'react-router-dom';
import {
    FaEdit,
    FaEye,
    FaRegCopy,
} from 'react-icons/fa';

import SmartTopTool from '../../../components/Smart/SmartTopTool';
import SmartPagination from '../../../components/Smart/SmartPagination';
import SmartTable from '../../../components/Smart/SmartTable';
import API from '../../../api/axios';
import { useFormat } from '../../../components/FormatContext';

const SEARCH_TYPE = [
    {
        key: 'code',
        label: 'Code',
    },
    {
        key: 'validEmail',
        label: 'Email',
    },
    {
        key: 'orderId',
        label: 'Order ID',
    },
    {
        key: 'redeemShopId',
        label: 'Redeem Shop ID',
    },
];

const DATE_OPTIONS = [
    'createdAt',
    'validStartDate',
    'validEndDate',
];

const FILTER_OPTIONS = {
    redeemScope: [
        'SAME_SHOP',
        'ANY_SHOP',
    ],
    isActive: [
        'true',
        'false',
    ],
    isUsed: [
        'true',
        'false',
    ],
    notified: [
        'true',
        'false',
    ],
    expired: [
        'true',
        'false',
    ],
};

const DEFAULT_FILTERS = {
    dateFilters: [],
    redeemScope: [],
    isActive: [],
    isUsed: [],
    notified: [],
    expired: [],
};

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
    } catch {
        // ignore
    }
};

const pickSingle = (arr) => {
    if (
        Array.isArray(arr) &&
        arr.length === 1
    ) {
        return arr[0];
    }

    return null;
};

const renderScope = (item) => {
    const scope = item?.redeemScope;

    if (scope === 'ANY_SHOP') {
        return (
            <span>
                Any shop
            </span>
        );
    }

    if (scope === 'SAME_SHOP') {
        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip>
                        {item?.redeemShopId
                            ? `ShopId: ${item.redeemShopId}`
                            : 'Shop restricted'}
                    </Tooltip>
                }
            >
                <span
                    style={{
                        cursor: 'help',
                    }}
                >
                    Same shop
                </span>
            </OverlayTrigger>
        );
    }

    return (
        <span className="text-muted">
            {scope || '—'}
        </span>
    );
};

const renderAmount = (item) => {
    const amount =
        item?.discount ?? '0.00';

    const currency =
        item?.currency ?? '';

    return (
        <span className="fw-semibold">
            {currency} {amount}
        </span>
    );
};

const renderStatusBadges = (item) => {
    const active =
        !!item?.isActive;

    const used =
        !!item?.isUsed;

    const expired =
        !!item?.isExpired;

    const notified =
        !!item?.notifiedAt;

    return (
        <div className="d-flex gap-1 flex-wrap">
            <Badge
                bg={
                    active
                        ? 'success'
                        : 'secondary'
                }
            >
                {active
                    ? 'Active'
                    : 'Inactive'}
            </Badge>

            <Badge
                bg={
                    used
                        ? 'primary'
                        : 'light'
                }
                text={
                    used
                        ? undefined
                        : 'dark'
                }
            >
                {used
                    ? 'Used'
                    : 'Unused'}
            </Badge>

            {expired ? (
                <Badge bg="danger">
                    Expired
                </Badge>
            ) : (
                <Badge
                    bg="light"
                    text="dark"
                >
                    Valid
                </Badge>
            )}

            <Badge
                bg={
                    notified
                        ? 'info'
                        : 'light'
                }
                text={
                    notified
                        ? undefined
                        : 'dark'
                }
            >
                {notified
                    ? 'Notified'
                    : 'Not sent'}
            </Badge>
        </div>
    );
};

const CreditCodesPage = () => {
    const {
        baseFormatters,
    } = useFormat();

    const [page, setPage] =
        useState({
            page_number: 1,
            total_pages: 1,
        });

    const [meta, setMeta] =
        useState({
            total: 0,
            page: 1,
            pageSize: 10,
            totalPages: 1,
        });

    const [rows, setRows] =
        useState([]);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState('');

    const [
        searchConditions,
        setSearchConditions,
    ] = useState([]);

    const [filters, setFilters] =
        useState(DEFAULT_FILTERS);

    const fetchCreditCodes =
        useCallback(async () => {
            try {
                setLoading(true);
                setError('');

                const params =
                    new URLSearchParams();

                params.set(
                    'page',
                    String(
                        page.page_number ||
                        1,
                    ),
                );

                params.set(
                    'pageSize',
                    String(
                        meta.pageSize ||
                        10,
                    ),
                );

                searchConditions.forEach(
                    ({
                        type,
                        value,
                    }) => {
                        if (
                            value == null ||
                            String(value).trim() ===
                            ''
                        ) {
                            return;
                        }

                        const normalizedValue =
                            String(
                                value,
                            ).trim();

                        switch (type) {
                            case 'code':
                            case 'validEmail':
                            case 'orderId':
                            case 'redeemShopId':
                                params.set(
                                    type,
                                    normalizedValue,
                                );
                                break;

                            default:
                                break;
                        }
                    },
                );

                filters.dateFilters.forEach(
                    ({
                        type,
                        operator,
                        values = [],
                    }) => {
                        if (!type) {
                            return;
                        }

                        const value1 =
                            values[0];

                        const value2 =
                            values[1];

                        if (
                            type ===
                            'createdAt'
                        ) {
                            switch (
                            operator
                            ) {
                                case 'between':
                                    if (
                                        value1
                                    ) {
                                        params.set(
                                            'createdAtFrom',
                                            value1,
                                        );
                                    }

                                    if (
                                        value2
                                    ) {
                                        params.set(
                                            'createdAtTo',
                                            value2,
                                        );
                                    }
                                    break;

                                case 'before':
                                    if (
                                        value1
                                    ) {
                                        params.set(
                                            'createdAtTo',
                                            value1,
                                        );
                                    }
                                    break;

                                case 'after':
                                    if (
                                        value1
                                    ) {
                                        params.set(
                                            'createdAtFrom',
                                            value1,
                                        );
                                    }
                                    break;

                                default:
                                    break;
                            }

                            return;
                        }

                        if (
                            type ===
                            'validStartDate'
                        ) {
                            switch (
                            operator
                            ) {
                                case 'between':
                                case 'after':
                                    if (
                                        value1
                                    ) {
                                        params.set(
                                            'validStartDateFrom',
                                            value1,
                                        );
                                    }
                                    break;

                                default:
                                    break;
                            }

                            return;
                        }

                        if (
                            type ===
                            'validEndDate'
                        ) {
                            switch (
                            operator
                            ) {
                                case 'between':
                                    if (
                                        value2
                                    ) {
                                        params.set(
                                            'validEndDateTo',
                                            value2,
                                        );
                                    }
                                    break;

                                case 'before':
                                    if (
                                        value1
                                    ) {
                                        params.set(
                                            'validEndDateTo',
                                            value1,
                                        );
                                    }
                                    break;

                                default:
                                    break;
                            }
                        }
                    },
                );

                const redeemScope =
                    pickSingle(
                        filters.redeemScope,
                    );

                if (redeemScope) {
                    params.set(
                        'redeemScope',
                        redeemScope,
                    );
                }

                const isActive =
                    pickSingle(
                        filters.isActive,
                    );

                if (isActive) {
                    params.set(
                        'isActive',
                        isActive,
                    );
                }

                const isUsed =
                    pickSingle(
                        filters.isUsed,
                    );

                if (isUsed) {
                    params.set(
                        'isUsed',
                        isUsed,
                    );
                }

                const notified =
                    pickSingle(
                        filters.notified,
                    );

                if (notified) {
                    params.set(
                        'notified',
                        notified,
                    );
                }

                const expired =
                    pickSingle(
                        filters.expired,
                    );

                if (expired) {
                    params.set(
                        'expired',
                        expired,
                    );
                }

                const queryString =
                    params.toString();

                const url =
                    queryString
                        ? `/codes/credits?${queryString}`
                        : '/codes/credits';

                const { data } =
                    await API.get(url);

                setRows(
                    data?.data || [],
                );

                const nextMeta =
                    data?.meta || {
                        total: 0,
                        page: 1,
                        pageSize: 10,
                        totalPages: 1,
                    };

                setMeta(nextMeta);

                const nextPage =
                    nextMeta.page || 1;

                const nextTotalPages =
                    nextMeta.totalPages ||
                    1;

                setPage((prev) => {
                    if (
                        prev.page_number ===
                        nextPage &&
                        prev.total_pages ===
                        nextTotalPages
                    ) {
                        return prev;
                    }

                    return {
                        ...prev,
                        page_number:
                            nextPage,
                        total_pages:
                            nextTotalPages,
                    };
                });
            } catch (e) {
                setError(
                    e?.response?.data
                        ?.message ||
                    e?.message ||
                    'Load failed',
                );
            } finally {
                setLoading(false);
            }
        }, [
            page.page_number,
            meta.pageSize,
            searchConditions,
            filters.dateFilters,
            filters.redeemScope,
            filters.isActive,
            filters.isUsed,
            filters.notified,
            filters.expired,
        ]);

    const handleSetSearchConditions =
        useCallback(
            (valueOrUpdater) => {
                setSearchConditions(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) => {
                    if (
                        prev.page_number ===
                        1
                    ) {
                        return prev;
                    }

                    return {
                        ...prev,
                        page_number: 1,
                    };
                });
            },
            [],
        );

    const handleSetFilters =
        useCallback(
            (valueOrUpdater) => {
                setFilters(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) => {
                    if (
                        prev.page_number ===
                        1
                    ) {
                        return prev;
                    }

                    return {
                        ...prev,
                        page_number: 1,
                    };
                });
            },
            [],
        );

    useEffect(() => {
        fetchCreditCodes();
    }, [fetchCreditCodes]);

    const columns = useMemo(
        () => [
            {
                key: 'code',
                label: 'Code',
                render: (item) => (
                    <div className="d-flex align-items-center gap-2">
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip>
                                    {item.code}
                                </Tooltip>
                            }
                        >
                            <span
                                className="fw-semibold"
                                style={{
                                    cursor: 'help',
                                }}
                            >
                                {item.code}
                            </span>
                        </OverlayTrigger>

                        <button
                            type="button"
                            className="btn btn-link p-0"
                            title="Copy"
                            onClick={() =>
                                copyToClipboard(
                                    item.code,
                                )
                            }
                        >
                            <FaRegCopy />
                        </button>
                    </div>
                ),
            },
            {
                key: 'amount',
                label: 'Amount',
                render: renderAmount,
            },
            {
                key: 'email',
                label: 'Email',
                render: (item) => (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip>
                                {
                                    item.validEmail
                                }
                            </Tooltip>
                        }
                    >
                        <span
                            style={{
                                cursor: 'help',
                            }}
                        >
                            {(item.validEmail ||
                                '').length >
                                22
                                ? `${item.validEmail.slice(
                                    0,
                                    21,
                                )}…`
                                : item.validEmail ||
                                '—'}
                        </span>
                    </OverlayTrigger>
                ),
            },
            {
                key: 'validity',
                label: 'Validity',
                render: (item) => (
                    <div className="d-flex flex-column">
                        <span>
                            {baseFormatters.date.numeric.format(
                                new Date(
                                    item.validStartDate,
                                ),
                            )}
                        </span>

                        <span>
                            {baseFormatters.date.numeric.format(
                                new Date(
                                    item.validEndDate,
                                ),
                            )}
                        </span>
                    </div>
                ),
            },
            {
                key: 'scope',
                label: 'Scope',
                render: renderScope,
            },
            {
                key: 'status',
                label: 'Status',
                render:
                    renderStatusBadges,
            },
            {
                key: 'createdAt',
                label: 'Created',
                render: (item) =>
                    item.createdAt ? (
                        <span>
                            {baseFormatters.date.numeric.format(
                                new Date(
                                    item.createdAt,
                                ),
                            )}
                        </span>
                    ) : (
                        <span className="text-muted">
                            —
                        </span>
                    ),
            },
            {
                key: 'actions',
                label: 'Actions',
                render: (item) => (
                    <div className="d-flex gap-2 align-items-center">
                        <NavLink
                            to={`/codes/credits/${item.id}`}
                            className="btn btn-view p-0"
                            title="View / Edit"
                        >
                            <FaEye
                                size={
                                    20
                                }
                            />
                        </NavLink>

                        <NavLink
                            to={`/codes/credits/${item.id}`}
                            className="btn btn-edit p-0"
                            title="Edit"
                        >
                            <FaEdit
                                size={
                                    20
                                }
                                style={{
                                    marginLeft:
                                        '4px',
                                }}
                            />
                        </NavLink>
                    </div>
                ),
            },
        ],
        [baseFormatters],
    );

    return (
        <Container
            fluid
            className="d-flex flex-column"
        >
            <Row className="mb-4">
                <Col sm={8}>
                    <span className="fw-semibold">
                        Credit Codes
                    </span>
                </Col>

                <Col
                    sm={4}
                    className="text-end"
                >
                    <span className="fw-semibold">
                        <NavLink
                            to="/dashboard"
                            className="block-link"
                        >
                            Dashboard
                        </NavLink>{' '}
                        - Credit Codes
                    </span>
                </Col>
            </Row>

            <Row>
                <Col sm={12}>
                    <Card className="h-100 p-2">
                        <Card.Body className="px-2 pt-2 pb-5">
                            <Row>
                                <Col md={10}>
                                    <SmartTopTool
                                        searchType={
                                            SEARCH_TYPE
                                        }
                                        searchConditions={
                                            searchConditions
                                        }
                                        setSearchConditions={
                                            handleSetSearchConditions
                                        }
                                        dateOptions={
                                            DATE_OPTIONS
                                        }
                                        filterOptions={
                                            FILTER_OPTIONS
                                        }
                                        filters={
                                            filters
                                        }
                                        setFilters={
                                            handleSetFilters
                                        }
                                    />
                                </Col>

                                <Col
                                    md={2}
                                    className="d-flex justify-content-end"
                                >
                                    <NavLink
                                        to="/codes/credits/new"
                                        className="border ms-1 btn-custom-success btn btn-primary"
                                        title="New"
                                        style={{
                                            height: '40px',
                                        }}
                                    >
                                        + New
                                    </NavLink>
                                </Col>

                                <Col md={12}>
                                    <SmartTable
                                        data={
                                            rows
                                        }
                                        columns={
                                            columns
                                        }
                                        loading={
                                            loading
                                        }
                                        emptyState={
                                            <div className="text-center py-5 text-muted">
                                                <p>
                                                    {error ||
                                                        'No matching credit codes found'}
                                                </p>
                                            </div>
                                        }
                                    />
                                </Col>

                                <Col
                                    md={12}
                                    className="d-flex justify-content-end"
                                >
                                    <SmartPagination
                                        page={
                                            page
                                        }
                                        setPage={
                                            setPage
                                        }
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default CreditCodesPage;